import {
  createContext,
  Dispatch,
  Fragment,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IntlProvider } from 'react-intl';

import { I18N_LOG_MISSING_TRANSLATIONS } from '@/config/runtime-env';
import { useAuthContext } from '@/contexts/auth-context';
import {
  getInitialBrowserLocale,
  resolveUserLocaleString,
} from '@/i18n/resolve-user-locale';

import { translationMessages } from '../messages';
import {
  AvailableLocale,
  defaultRichTextElements,
  timezone as defaultTimezone,
} from './shared';

export type LanguageContextType = {
  locale: AvailableLocale;
  setLocale: Dispatch<SetStateAction<AvailableLocale>>;
  timezone: string;
  setTimezone: Dispatch<SetStateAction<string>>;
};

export const LanguageContext = createContext<LanguageContextType>(
  // Default context
  {
    locale: getInitialBrowserLocale(),
    setLocale: () => void 0,
    timezone: defaultTimezone,
    setTimezone: () => void 0,
  },
);

export default function LanguageProvider({ children }: PropsWithChildren) {
  const { whoami, isLoading } = useAuthContext();
  const browserLocale = useMemo(() => getInitialBrowserLocale(), []);

  const [locale, setLocale] = useState<AvailableLocale>(browserLocale);
  const [timezone, setTimezone] = useState(defaultTimezone);

  useEffect(() => {
    if (isLoading) return;

    if (whoami?.locale) {
      setLocale(resolveUserLocaleString(whoami.locale, browserLocale));
    } else {
      setLocale(browserLocale);
    }

    if (whoami?.time_zone) {
      setTimezone(whoami.time_zone);
    } else {
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  }, [browserLocale, isLoading, whoami]);

  const handleError = (error: Error & { code: string }) => {
    if (
      I18N_LOG_MISSING_TRANSLATIONS === 'true' &&
      error.code === 'MISSING_TRANSLATION'
    ) {
      // Make missing translations errors less visible as they are very verbose and can hide more valuable error messages
      // eslint-disable-next-line no-console
      console.warn(error.message);
    }
  };

  return (
    <LanguageContext.Provider
      value={{ locale, setLocale, timezone, setTimezone }}
    >
      <IntlProvider
        locale={locale}
        key={locale}
        messages={translationMessages[locale]}
        textComponent={Fragment}
        defaultRichTextElements={defaultRichTextElements}
        onError={handleError}
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
}
